import * as moment from 'moment';

import { effectiveDurationMinutes } from './appointment-duration';
import { eventDayEntries, isScheduleEvent } from './schedule-events';
import { effectivePatientsLimit, effectivePatientsPerTimeSlot } from './slot-capacity';

/**
 * Which booking slots are still open, shared by the open-slots grid and the calendar.
 *
 * A slot counts the timed bookings that overlap it; cancelled and did-not-attend bookings do not
 * hold their slot. A slot is full when the practice's patients-per-slot capacity is reached (never
 * for an unlimited practice) or when a busy event overlaps it, all-day busy events included. An
 * event that is not busy is listed in the slot but takes no place. Events may arrive as the month
 * endpoint sends them (startDate startTime to endDate endTime) or as per-day calendar copies.
 */

export type OpenSlotState = 'free' | 'full' | 'past';

export interface OpenSlotCell {
  time: string;
  state: OpenSlotState;
  /** Bookings already in the slot. */
  taken: number;
  /** Places left in the slot; null when the practice takes unlimited patients per slot. */
  remaining: number | null;
  /** A busy event fills the slot. */
  busy: boolean;
  /** What is in the slot: bookings first, then events. */
  items: OpenSlotItem[];
}

export interface OpenSlotItem {
  kind: 'booking' | 'event';
  /** "Surname, Name" for a booking, the title for an event. */
  title: string;
  /** Appointment type of a booking. */
  detail: string;
  color: string | null;
  busy: boolean;
  patientId: string | null;
}

export interface OpenSlotDay {
  date: Date;
  dateKey: string;
  cells: OpenSlotCell[];
}

export interface OpenSlotSchedule {
  startTime?: string;
  endTime?: string;
  timeSlotDuration?: string | number;
  patientsLimit?: string;
  patientsPerTimeSlot?: number | string;
}

/** Slot start times (`HH:mm`) for slots that end no later than `endTime`. */
export function slotTimes(startTime: string, endTime: string, durationMinutes: number): string[] {
  const toMinutes = (t: string) => {
    const [h, m] = (t || '00:00').split(':').map(Number);
    return h * 60 + m;
  };
  if (!(durationMinutes > 0)) return [];
  const end = toMinutes(endTime);
  const times: string[] = [];
  for (let start = toMinutes(startTime); start + durationMinutes <= end; start += durationMinutes) {
    times.push(`${String(Math.floor(start / 60)).padStart(2, '0')}:${String(start % 60).padStart(2, '0')}`);
  }
  return times;
}

/** The first `count` days from `from` (inclusive) whose weekday name is in `availableDays`. */
export function workingDays(from: Date, count: number, availableDays: string[]): Date[] {
  const days: Date[] = [];
  if (!availableDays?.length) return days;
  const cursor = moment(from).startOf('day');
  for (let i = 0; days.length < count && i < 366; i++) {
    if (availableDays.includes(cursor.format('dddd'))) days.push(cursor.toDate());
    cursor.add(1, 'day');
  }
  return days;
}

/** The last `count` working days before `before` (exclusive), oldest first. */
export function workingDaysBefore(before: Date, count: number, availableDays: string[]): Date[] {
  const days: Date[] = [];
  if (!availableDays?.length) return days;
  const cursor = moment(before).startOf('day').subtract(1, 'day');
  for (let i = 0; days.length < count && i < 366; i++) {
    if (availableDays.includes(cursor.format('dddd'))) days.unshift(cursor.toDate());
    cursor.subtract(1, 'day');
  }
  return days;
}

export function computeOpenSlots(
  dates: Date[],
  appointments: any[],
  schedule: OpenSlotSchedule | null | undefined,
  now: Date = new Date(),
): OpenSlotDay[] {
  const duration = Number(schedule?.timeSlotDuration) || effectiveDurationMinutes(null, schedule);
  const times = slotTimes(schedule?.startTime || '00:00', schedule?.endTime || '23:59', duration);
  const unlimited = effectivePatientsLimit(schedule) === 'unlimited';
  const capacity = effectivePatientsPerTimeSlot(schedule);
  const nowMoment = moment(now);

  return dates.map(date => {
    const day = moment(date).startOf('day');
    const entries = (appointments || [])
      .flatMap(a => (a && !a.date && a.startDate ? eventDayEntries(a) : [a]))
      .filter(a => a?.date && moment(a.date).isSame(day, 'day'));
    const span = (a: any) => {
      const start = atTime(day, a.timeSlot);
      const end = a.endSlot
        ? atTime(day, a.endSlot)
        : start.clone().add(effectiveDurationMinutes(a.typeDetails, { timeSlotDuration: duration }), 'minutes');
      return { start, end };
    };
    const bookings = entries
      .filter(a => !isScheduleEvent(a) && a.timeSlot && !a.isAllDay && !a.isAvailable && !a.isCancelled && !a.didNotAttend)
      .map(a => ({ ...span(a), item: bookingItem(a) }));
    const events = entries
      .filter(isScheduleEvent)
      .map(a => ({
        ...(a.isAllDay || !a.timeSlot ? { start: day.clone(), end: day.clone().add(1, 'day') } : span(a)),
        item: eventItem(a),
      }));

    const cells = times.map(time => {
      const slotStart = atTime(day, time);
      const slotEnd = slotStart.clone().add(duration, 'minutes');
      const inSlot = (b: { start: moment.Moment; end: moment.Moment }) => slotStart.isBefore(b.end) && slotEnd.isAfter(b.start);
      const items = [...bookings.filter(inSlot), ...events.filter(inSlot)].map(b => b.item);
      const taken = items.filter(i => i.kind === 'booking').length;
      const busy = items.some(i => i.kind === 'event' && i.busy);
      const cell = { time, taken, busy, items };
      if (!slotEnd.isAfter(nowMoment)) return { ...cell, state: 'past' as const, remaining: unlimited ? null : 0 };
      if (busy) return { ...cell, state: 'full' as const, remaining: unlimited ? null : 0 };
      if (unlimited) return { ...cell, state: 'free' as const, remaining: null };
      const remaining = Math.max(capacity - taken, 0);
      return { ...cell, state: remaining > 0 ? 'free' as const : 'full' as const, remaining };
    });

    return { date: day.toDate(), dateKey: day.format('YYYY-MM-DD'), cells };
  });
}

function bookingItem(a: any): OpenSlotItem {
  const patient = a.Patient;
  return {
    kind: 'booking',
    title: patient ? [patient.surname, patient.name].filter(Boolean).join(', ') : '',
    detail: a.appointmentType || a.typeDetails?.type || '',
    color: a.typeDetails?.backgroundColor || null,
    busy: false,
    patientId: patient?.id ?? a.patientId ?? null,
  };
}

function eventItem(a: any): OpenSlotItem {
  return {
    kind: 'event',
    title: String(a.reason || '').trim(),
    detail: '',
    color: a.backgroundColor || null,
    busy: a.showAsBusy === true,
    patientId: null,
  };
}

function atTime(day: moment.Moment, time: string): moment.Moment {
  const [h, m] = time.split(':').map(Number);
  return day.clone().set({ hour: h, minute: m, second: 0, millisecond: 0 });
}
